import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { LeaderboardService, LeaderboardEntry } from '../services/leaderboardService';
import { gameModeConfigs } from '../data/gameObjects';
import { useAuth } from '../hooks/useAuth';
import { useOffline } from '../hooks/useOffline';
import audioFileService from '../services/audioFileService';

interface LeaderboardScreenProps {
  onBack: () => void;
}

export const LeaderboardScreen: React.FC<LeaderboardScreenProps> = ({ onBack }) => {
  const { user } = useAuth();
  const { isOffline } = useOffline();
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedGameMode, setSelectedGameMode] = useState<string>('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>('all');
  const [userRank, setUserRank] = useState<{ rank: number; total: number; userScores: number; bestScore: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadLeaderboard = async () => {
    if (isOffline) {
      setError('Leaderboard requires an internet connection');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const filters: any = {};
      if (selectedGameMode !== 'all') {
        filters.gameMode = selectedGameMode;
      }
      if (selectedDifficulty !== 'all') {
        filters.difficulty = selectedDifficulty;
      }
      
      const leaderboardData = await LeaderboardService.getRecentLeaderboard(filters);
      setEntries(leaderboardData);

      // Get user's rank if authenticated
      if (user) {
        const rankData = await LeaderboardService.getUserRank(
          user.id, 
          selectedGameMode !== 'all' ? selectedGameMode : undefined
        );
        setUserRank(rankData);
      }

    } catch (err) {
      console.error('Error loading leaderboard:', err);
      setError('Failed to load leaderboard. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLeaderboard();
  }, [selectedGameMode, selectedDifficulty, isOffline, user]);

  const handleGameModeChange = (gameMode: string) => {
    audioFileService.play('click');
    setSelectedGameMode(gameMode);
  };

  const handleDifficultyChange = (difficulty: string) => {
    audioFileService.play('click');
    setSelectedDifficulty(difficulty);
  };

  const formatTimeAgo = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    } else {
      const hours = Math.floor(diffInMinutes / 60);
      return `${hours}h ago`;
    }
  };

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getGameModeIcon = (gameMode: string) => {
    const config = gameModeConfigs.find(config => config.id === gameMode);
    return config?.icon || '🎮';
  };

  const getGameModeName = (gameMode: string) => {
    const config = gameModeConfigs.find(config => config.id === gameMode);
    return config?.name || gameMode;
  };

  const getDifficultyIcon = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return '🟢';
      case 'medium': return '🟡';
      case 'hard': return '🔴';
      case 'mixed': return '🔀';
      default: return '❓';
    }
  };

  const getDifficultyName = (difficulty: string) => {
    return difficulty.charAt(0).toUpperCase() + difficulty.slice(1);
  };

  if (isOffline) {
    return (
      <div className="max-w-4xl mx-auto text-center">
        <div className="card">
          <div className="text-6xl mb-4">📡</div>
          <h2 className="text-2xl font-bold text-banana-800 mb-4">Offline Mode</h2>
          <p className="text-banana-700 mb-6">
            The leaderboard requires an internet connection to show global rankings.
          </p>
          <button onClick={onBack} className="btn-primary">
            🏠 Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <motion.div
        className="text-center mb-8"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="flex items-center justify-center gap-4 mb-4">
          <button
            onClick={onBack}
            className="btn-secondary px-4 py-2"
            title="Back to Home"
          >
            ← Back
          </button>
          <h1 className="text-3xl md:text-4xl font-display font-bold text-banana-800">
            🏆 Leaderboard
          </h1>
        </div>
        <p className="text-banana-700">
          Top scores from the past week
        </p>
      </motion.div>

      {/* User's Rank (if authenticated) */}
      {user && userRank && userRank.rank > 0 && (
        <motion.div
          className="card mb-6 bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-300"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="text-center">
            <h3 className="text-lg font-bold text-orange-800 mb-2">Your Ranking</h3>
            <div className="flex justify-center items-center gap-4">
              <div className="text-2xl font-bold text-orange-600">
                #{userRank.rank}
              </div>
              <div className="text-orange-700">
                {userRank.total === 1 ? (
                  `Your best: ${userRank.bestScore} pts (${userRank.userScores} game${userRank.userScores !== 1 ? 's' : ''})`
                ) : (
                  `out of ${userRank.total} players`
                )}
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Game Mode Filter */}
      <motion.div
        className="card mb-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
      >
        <h3 className="text-lg font-bold text-banana-800 mb-4 text-center">Filter by Game Mode</h3>
        <div className="flex flex-wrap justify-center gap-2">
          <button
            className={`px-4 py-2 rounded-lg border-2 transition-all text-sm ${
              selectedGameMode === 'all'
                ? 'border-banana-500 bg-banana-100 text-banana-800'
                : 'border-banana-200 bg-white text-banana-700 hover:border-banana-300'
            }`}
            onClick={() => handleGameModeChange('all')}
          >
            🎯 All Modes
          </button>
          {gameModeConfigs.map((config) => (
            <button
              key={config.id}
              className={`px-4 py-2 rounded-lg border-2 transition-all text-sm ${
                selectedGameMode === config.id
                  ? 'border-banana-500 bg-banana-100 text-banana-800'
                  : 'border-banana-200 bg-white text-banana-700 hover:border-banana-300'
              }`}
              onClick={() => handleGameModeChange(config.id)}
            >
              {config.icon} {config.name}
            </button>
          ))}
        </div>
      </motion.div>

      {/* Difficulty Filter */}
      <motion.div
        className="card mb-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.35 }}
      >
        <h3 className="text-lg font-bold text-banana-800 mb-4 text-center">Filter by Difficulty</h3>
        <div className="flex flex-wrap justify-center gap-2">
          <button
            className={`px-4 py-2 rounded-lg border-2 transition-all text-sm ${
              selectedDifficulty === 'all'
                ? 'border-banana-500 bg-banana-100 text-banana-800'
                : 'border-banana-200 bg-white text-banana-700 hover:border-banana-300'
            }`}
            onClick={() => handleDifficultyChange('all')}
          >
            🎯 All Difficulties
          </button>
          <button
            className={`px-4 py-2 rounded-lg border-2 transition-all text-sm ${
              selectedDifficulty === 'easy'
                ? 'border-banana-500 bg-banana-100 text-banana-800'
                : 'border-banana-200 bg-white text-banana-700 hover:border-banana-300'
            }`}
            onClick={() => handleDifficultyChange('easy')}
          >
            🟢 Easy
          </button>
          <button
            className={`px-4 py-2 rounded-lg border-2 transition-all text-sm ${
              selectedDifficulty === 'medium'
                ? 'border-banana-500 bg-banana-100 text-banana-800'
                : 'border-banana-200 bg-white text-banana-700 hover:border-banana-300'
            }`}
            onClick={() => handleDifficultyChange('medium')}
          >
            🟡 Medium
          </button>
          <button
            className={`px-4 py-2 rounded-lg border-2 transition-all text-sm ${
              selectedDifficulty === 'hard'
                ? 'border-banana-500 bg-banana-100 text-banana-800'
                : 'border-banana-200 bg-white text-banana-700 hover:border-banana-300'
            }`}
            onClick={() => handleDifficultyChange('hard')}
          >
            🔴 Hard
          </button>
          <button
            className={`px-4 py-2 rounded-lg border-2 transition-all text-sm ${
              selectedDifficulty === 'mixed'
                ? 'border-banana-500 bg-banana-100 text-banana-800'
                : 'border-banana-200 bg-white text-banana-700 hover:border-banana-300'
            }`}
            onClick={() => handleDifficultyChange('mixed')}
          >
            🔀 Mixed
          </button>
        </div>
      </motion.div>

      {/* Leaderboard */}
      <motion.div
        className="card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
      >
        {loading ? (
          <div className="text-center py-8">
            <div className="text-4xl mb-4">⏳</div>
            <p className="text-banana-700">Loading leaderboard...</p>
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <div className="text-4xl mb-4">❌</div>
            <p className="text-red-600 mb-4">{error}</p>
            <button onClick={loadLeaderboard} className="btn-secondary">
              Try Again
            </button>
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-4xl mb-4">🎮</div>
            <p className="text-banana-700">
              No games played in the past week
              {(selectedGameMode !== 'all' || selectedDifficulty !== 'all') && (
                <span>
                  {' for '}
                  {selectedGameMode !== 'all' && getGameModeName(selectedGameMode)}
                  {selectedGameMode !== 'all' && selectedDifficulty !== 'all' && ' - '}
                  {selectedDifficulty !== 'all' && `${selectedDifficulty.charAt(0).toUpperCase() + selectedDifficulty.slice(1)} difficulty`}
                </span>
              )}
            </p>
            <p className="text-banana-600 text-sm mt-2">
              Be the first to set a score!
            </p>
          </div>
        ) : (
          <div>
            <h3 className="text-xl font-bold text-banana-800 mb-4 text-center">
              Top {entries.length} Scores
              {(selectedGameMode !== 'all' || selectedDifficulty !== 'all') && (
                <span>
                  {' - '}
                  {selectedGameMode !== 'all' && getGameModeName(selectedGameMode)}
                  {selectedGameMode !== 'all' && selectedDifficulty !== 'all' && ' - '}
                  {selectedDifficulty !== 'all' && `${selectedDifficulty.charAt(0).toUpperCase() + selectedDifficulty.slice(1)} Difficulty`}
                </span>
              )}
            </h3>
            <div className="space-y-3">
              {entries.map((entry, index) => (
                <motion.div
                  key={entry.id}
                  className={`flex items-center p-4 rounded-lg border-2 transition-all ${
                    index < 3
                      ? 'border-yellow-300 bg-gradient-to-r from-yellow-50 to-orange-50'
                      : 'border-banana-200 bg-white hover:border-banana-300'
                  }`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                >
                  {/* Rank */}
                  <div className="flex-shrink-0 w-12 text-center">
                    <div className={`text-2xl font-bold ${
                      index === 0 ? 'text-yellow-600' :
                      index === 1 ? 'text-gray-500' :
                      index === 2 ? 'text-orange-600' :
                      'text-banana-700'
                    }`}>
                      {index < 3 ? ['🥇', '🥈', '🥉'][index] : `#${index + 1}`}
                    </div>
                  </div>

                  {/* Player Info */}
                  <div className="flex-grow ml-4">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xl">{entry.avatar}</span>
                      <span className="font-bold text-banana-800">{entry.username}</span>
                      {user?.id === entry.userId && (
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">You</span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-banana-600">
                      <span>🎯 {entry.score} pts</span>
                      <span>🔥 {entry.streak} streak</span>
                      <span>✅ {entry.accuracy}% accuracy</span>
                      <span>⏱️ {formatDuration(entry.timePlayed)}</span>
                      {selectedGameMode === 'all' && (
                        <span>{getGameModeIcon(entry.gameMode)} {getGameModeName(entry.gameMode)}</span>
                      )}
                      {selectedDifficulty === 'all' && (
                        <span>{getDifficultyIcon(entry.difficulty)} {getDifficultyName(entry.difficulty)}</span>
                      )}
                      <span className="text-gray-500">{formatTimeAgo(entry.achievedAt)}</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}; 